import { PDFDocument, registerFile } from "pdfkit";
import logoImg from "../assets/images/jpg/logo.jpg";
import fontRegularUrl from "../assets/fonts/DMSans-Medium.ttf";
import fontBoldUrl from "../assets/fonts/DMSans-Bold.ttf";
import { getPrintDate } from "./functions";
import UserStore from "../store/UserStore";

// CSS px -> PDF pt (96 px/in, 72pt/in).
const PT_PER_PX = 72 / 96;
function pxToPt(px) {
  return px * PT_PER_PX;
}

// Normalizes a column width given as a number or a px string (e.g. "330px")
// into a plain numeric px value; returns null if it isn't a usable width.
function parsePxWidth(width) {
  if (typeof width === "number") {
    return Number.isFinite(width) ? width / 2.75 : null;
  }
  if (typeof width === "string") {
    const match = width.trim().match(/^(-?\d+(?:\.\d+)?)\s*(px)?$/i);
    if (match) return parseFloat(match[1]) / 2.75;
  }
  return null;
}

// Solves fixed/flexible column widths and a single table font size so the
// table fills the page width, flexible columns stay snug to their content,
// and fixed-column headers never wrap onto a second line.
function computeAutoShrinkColumnWidths(
  doc,
  headers,
  rows,
  columnStyles = [],
  {
    headerFont = "Body-Bold.ttf",
    cellFont = "Body-Regular.ttf",
    maxFontSize = 8,
    minFontSize = 1,
    referenceFontSize = 10,
    cellPadding = 3,
    rightPadding = 4,
    cellBorder = 0.5,
    preferredTotalWidth,
  },
) {
  const n = headers.length;
  const extra = cellPadding + rightPadding + 2 * cellBorder + 1; // padding/border don't scale with font

  const alignments = new Array(n).fill(null);
  const columnWidths = new Array(n).fill(null);
  const flexIndices = [];
  let fixedTotal = 0;

  for (let i = 0; i < n; i++) {
    const style = columnStyles[i] || {};
    const defaultAlign = i === 0 ? "left" : "right";
    alignments[i] = style.align || defaultAlign;
    const widthPx = parsePxWidth(style.width);
    if (widthPx !== null) {
      const pt = pxToPt(widthPx);
      columnWidths[i] = pt;
      fixedTotal += pt;
    } else {
      flexIndices.push(i);
    }
  }

  if (flexIndices.length === 0 && fixedTotal > 0) {
    const scale = preferredTotalWidth / fixedTotal;
    for (let i = 0; i < n; i++) {
      columnWidths[i] *= scale;
    }
  }

  const availableForFlex = preferredTotalWidth - fixedTotal;

  const widthsAtRef = {};
  doc.font(headerFont).fontSize(referenceFontSize);
  flexIndices.forEach((i) => {
    widthsAtRef[i] = doc.widthOfString(String(headers[i] ?? ""));
  });
  doc.font(cellFont).fontSize(referenceFontSize);
  rows.forEach((row) => {
    flexIndices.forEach((i) => {
      const w = doc.widthOfString(String(row[i] ?? ""));
      if (w > widthsAtRef[i]) widthsAtRef[i] = w;
    });
  });

  const textWidthTotalAtRef = flexIndices.reduce(
    (a, i) => a + widthsAtRef[i],
    0,
  );
  const extraTotal = flexIndices.length * extra;
  const availableForText = availableForFlex - extraTotal;

  let fontSize;
  if (flexIndices.length === 0 || textWidthTotalAtRef <= 0) {
    fontSize = maxFontSize;
  } else if (availableForText <= 0) {
    fontSize = minFontSize;
  } else {
    fontSize = referenceFontSize * (availableForText / textWidthTotalAtRef);
    fontSize = Math.min(fontSize, maxFontSize);
    fontSize = Math.max(fontSize, minFontSize);
  }

  const fixedIndices = [];
  for (let i = 0; i < n; i++) {
    if (!flexIndices.includes(i)) fixedIndices.push(i);
  }
  if (fixedIndices.length > 0) {
    doc.font(headerFont).fontSize(referenceFontSize);
    fixedIndices.forEach((i) => {
      const headerWidthAtRef = doc.widthOfString(String(headers[i] ?? ""));
      if (headerWidthAtRef <= 0) return;
      const availableForHeader = columnWidths[i] - extra;
      if (availableForHeader <= 0) {
        fontSize = minFontSize;
        return;
      }
      const fontForThisHeader =
        referenceFontSize * (availableForHeader / headerWidthAtRef);
      if (fontForThisHeader < fontSize) fontSize = fontForThisHeader;
    });
    fontSize = Math.max(minFontSize, Math.min(maxFontSize, fontSize));
  }

  const flexWidthsRaw = flexIndices.map(
    (i) => (widthsAtRef[i] * fontSize) / referenceFontSize + extra,
  );
  const flexTotal = flexWidthsRaw.reduce((a, b) => a + b, 0);
  flexIndices.forEach((i, idx) => {
    columnWidths[i] = flexWidthsRaw[idx];
  });

  if (flexIndices.length > 0) {
    const diff = availableForFlex - flexTotal;
    if (fixedIndices.length > 0) {
      if (diff > 0 && fixedTotal > 0) {
        fixedIndices.forEach((i) => {
          columnWidths[i] += (columnWidths[i] / fixedTotal) * diff;
        });
      }
    } else if (flexTotal > 0) {
      flexIndices.forEach((i, idx) => {
        columnWidths[i] =
          flexWidthsRaw[idx] + (flexWidthsRaw[idx] / flexTotal) * diff;
      });
    }
  }

  return {
    fontSize,
    columnWidths,
    alignments,
    fitsPreferredWidth: flexIndices.length === 0 || fontSize > minFontSize,
  };
}

function measureTwoColumnBoxWidths(doc, rows, font, fontSize, padding, border) {
  doc.font(font).fontSize(fontSize);
  let labelWidth = 0;
  let valueWidth = 0;
  rows.forEach(([label, value]) => {
    labelWidth = Math.max(labelWidth, doc.widthOfString(String(label ?? "")));
    valueWidth = Math.max(valueWidth, doc.widthOfString(String(value ?? "")));
  });
  const extra = 2 * padding + 2 * border + 2;
  return [labelWidth + extra, valueWidth + extra];
}

function getGeneratedTimeAndDate() {
  const currTime = new Date();
  return `Report Generated  ${currTime.toLocaleTimeString(undefined, {
    hour: "2-digit",
    hour12: false,
  })}:${
    currTime.getMinutes() < 10
      ? `0${currTime.getMinutes()}`
      : currTime.getMinutes()
  } :: ${
    currTime.getDate() < 10 ? `0${currTime.getDate()}` : currTime.getDate()
  }/${
    currTime.getMonth() < 9
      ? `0${currTime.getMonth() + 1}`
      : currTime.getMonth() + 1
  }/${currTime.getFullYear()}`;
}

const CELL_PADDING = 2;
const RIGHT_PADDING = 3;
const CELL_BORDER = 0.5;
const MAX_FONT_SIZE = 8;
const MIN_FONT_SIZE = 1;
const REFERENCE_FONT_SIZE = 10;
const MARGIN = 15;
const TOP_PADDING = 44;
const HEADER_FONT_SIZE_RATIO = 1.7;
const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const PRIMARY_ROW_BACKGROUND_COLOR = "#FFE08A";
const LOGO_MAX_WIDTH = 110;
const LOGO_MIN_WIDTH = 80;
const LOGO_ASPECT_RATIO = 60 / 200;

export async function generateTransactionsReport({
  customFileName,
  headerName,
  isFamilyOnly,
  familyOrClientName,
  dateRange,
  columns,
  //  columnStyles,
  data,
  fileId = null,
  fileInfoTitle,
  disclaimer = {
    title: "Master Disclaimer",
    link: "https://www.nuvamaprivate.com/disclaimer/",
  },
}) {
  const [regularBytes, boldBytes, logoBytes] = await Promise.all([
    fetch(fontRegularUrl)
      .then((r) => r.arrayBuffer())
      .then((b) => new Uint8Array(b)),
    fetch(fontBoldUrl)
      .then((r) => r.arrayBuffer())
      .then((b) => new Uint8Array(b)),
    fetch(logoImg)
      .then((r) => r.arrayBuffer())
      .then((b) => new Uint8Array(b)),
  ]);
  registerFile("Body-Regular.ttf", regularBytes);
  registerFile("Body-Bold.ttf", boldBytes);
  registerFile("logo.jpg", logoBytes);

  let headers = [];
  let columnStylesFiltered = [];

  // For each visible column: get its header label (plain, or via
  // customHeaderRendererPdf/labelPdf for an object label).
  columns?.forEach((column, idx) => {
    if (!(column.showColumn || column.label)) return;
    const { customHeaderRendererPdf, ...rest } = column;
    const value = customHeaderRendererPdf
      ? customHeaderRendererPdf({ ...rest })
      : typeof rest.label === "object"
        ? rest.labelPdf
        : rest.label;
    headers.push(value);
    let data = {};

    // ...and build its parallel style entry (fixed width / pdfkit style , Only if provided for that column)
    // for computeAutoShrinkColumnWidths.
    if (column.width) {
      data.width = column.width;
    }
    if (column.pdfKitStyle) {
      data = { ...data, ...column.pdfKitStyle };
    }
    columnStylesFiltered.push(data);
  });

  // Same visible-column set as above, in the same order as headers/
  // columnStylesFiltered -- used below to pull each row's cell values via
  // cellRendererPdf (or the raw field) in that exact column order.
  const columnsFiltered = columns?.filter(
    (colum) => colum.showColumn || colum.label,
  );

  let rows = [];
  let rowIsPrimary = []; // parallel array -- rowIsPrimary[i] matches rows[i]
  data?.slice(0, 100)?.forEach((eachRow) => {
    let dataRow = [];
    columnsFiltered?.forEach((column) => {
      let value = undefined;
      if (column?.cellRendererPdf) {
        value = column?.cellRendererPdf(
          !column?.key2
            ? eachRow?.[column?.key]
            : eachRow?.[column?.key] || eachRow?.[column?.key2],
          eachRow,
          column?.key,
        );
      } else {
        value = eachRow[column.key];
      }
      dataRow.push(value);
    });
    rows.push(dataRow);
    rowIsPrimary.push(eachRow?.primary === true || eachRow?.isHeader === "1");
  });

  // No rows -> render only the heading/logo/details box/divider line below.
  const hasData = rows.length > 0;

  const measureDoc = new PDFDocument({ font: "Body-Regular.ttf" });
  measureDoc.on("data", () => {});

  const preferredContentWidth = PAGE_WIDTH - MARGIN * 2;
  const {
    fontSize: TABLE_FONT_SIZE,
    columnWidths,
    alignments: columnAlignments,
  } = computeAutoShrinkColumnWidths(
    measureDoc,
    headers,
    rows,
    columnStylesFiltered,
    {
      headerFont: "Body-Bold.ttf",
      cellFont: "Body-Regular.ttf",
      maxFontSize: MAX_FONT_SIZE,
      minFontSize: MIN_FONT_SIZE,
      referenceFontSize: REFERENCE_FONT_SIZE,
      cellPadding: CELL_PADDING,
      rightPadding: RIGHT_PADDING,
      cellBorder: CELL_BORDER,
      preferredTotalWidth: preferredContentWidth,
    },
  );
  measureDoc.end();

  const HEADER_FONT_SIZE = TABLE_FONT_SIZE * HEADER_FONT_SIZE_RATIO;

  const doc = new PDFDocument({
    size: [PAGE_WIDTH, PAGE_HEIGHT],
    margin: MARGIN,
    tagged: true,
    lang: "en-US",
    subset: "PDF/UA",
    pdfVersion: "1.5",
    font: "Body-Regular.ttf",
    displayTitle: true,
    info: {
      Title: fileInfoTitle,
      Author: "Nuvama",
    },
  });
  const chunks = [];
  doc.on("data", (chunk) => chunks.push(chunk));
  const done = new Promise((resolve) =>
    doc.on("end", () => resolve(new Blob(chunks, { type: "application/pdf" }))),
  );

  doc.outline.addItem(headerName);

  const documentStruct = doc.struct("Document");
  doc.addStructure(documentStruct);

  const pageLeft = doc.page.margins.left;
  const pageRight = doc.page.width - doc.page.margins.right;
  const contentWidth = pageRight - pageLeft;

  doc.y += TOP_PADDING;

  // 1. Heading
  documentStruct.add(
    doc.struct("H1", () => {
      doc
        .font("Body-Bold.ttf")
        .fontSize(HEADER_FONT_SIZE)
        .fillColor("#000000")
        .text(headerName, pageLeft, doc.y, {
          width: contentWidth,
          align: "center",
        });
    }),
  );
  doc.moveDown(2);

  const rowTop = doc.y;

  // 2 & 3. Logo + details box, wrapped as one row: both are vertically
  // centered against each other (like a flex row with align-items:
  // center), horizontal positions/spacing unchanged. The logo shrinks in
  // proportion to the table's solved font size -- smaller font (more
  // columns) gives a smaller logo -- clamped between LOGO_MIN_WIDTH and
  // LOGO_MAX_WIDTH/
  const logoWidth = Math.min(
    LOGO_MAX_WIDTH,
    Math.max(
      LOGO_MIN_WIDTH,
      LOGO_MAX_WIDTH * (TABLE_FONT_SIZE / MAX_FONT_SIZE),
    ),
  );
  const logoHeight = logoWidth * LOGO_ASPECT_RATIO;

  // 3. Details box
  const familyName =
    familyOrClientName ||
    (UserStore.getData === "fam"
      ? UserStore.userData?.family_name || "-"
      : UserStore.userData?.full_name || "-");
  const dateData = !dateRange ? getPrintDate(UserStore.asOfDate) : dateRange;
  const rmName =
    UserStore.exportDetails?.find((item) => item.role === "RM")?.name || "-";
  const smName =
    UserStore.exportDetails?.find((item) => item.role === "CXO")?.name || "-";

  const detailsRows = [
    [
      !isFamilyOnly ? "Client/Family/Account Name: " : "Family Name: ",
      familyName,
    ],
    ["Date Range:", dateData],
    ["Relationship Manager Name:", rmName],
    ["Service Manager Name:", smName],
  ];

  const detailsColumnWidths = measureTwoColumnBoxWidths(
    doc,
    detailsRows,
    "Body-Bold.ttf",
    TABLE_FONT_SIZE,
    CELL_PADDING,
    CELL_BORDER,
  );
  const detailsWidth = Math.min(
    detailsColumnWidths[0] + detailsColumnWidths[1],
    contentWidth,
  );
  const detailsX = pageRight - detailsWidth;

  doc.font("Body-Bold.ttf").fontSize(TABLE_FONT_SIZE);
  const detailsRowHeight =
    doc.currentLineHeight(true) + 2 * CELL_PADDING + 2 * CELL_BORDER;
  const detailsHeight = detailsRowHeight * detailsRows.length;

  // The wrapper: the taller of the two sets the row height, and each
  // element is pushed down from rowTop by half the leftover space --
  // same math as align-items: center on a flex row.
  const wrapperHeight = Math.max(logoHeight, detailsHeight);
  const logoY = rowTop + (wrapperHeight - logoHeight) / 2;
  const detailsY = rowTop + (wrapperHeight - detailsHeight) / 2;

  documentStruct.add(
    doc.struct(
      "Figure",
      {
        alt: "Nuvama Private logo",
        bbox: [pageLeft, logoY, pageLeft + logoWidth, logoY + logoHeight],
      },
      () => {
        doc.image("logo.jpg", pageLeft, logoY, { width: logoWidth });
      },
    ),
  );

  await doc.table({
    structParent: documentStruct,
    position: { x: detailsX, y: detailsY },
    columnStyles: detailsColumnWidths,
    rowStyles: () => ({
      font: { src: "Body-Bold.ttf", size: TABLE_FONT_SIZE },
      textColor: "#000000",
      border: CELL_BORDER,
      padding: CELL_PADDING,
    }),
    data: detailsRows.map(([label, value]) => [
      {
        text: label,
        type: "TH",
        scope: "row",
        align: { x: "right", y: "center" },
      },
      { text: value, align: { x: "right", y: "center" } },
    ]),
  });

  doc.y = rowTop + wrapperHeight + 12;

  // 4. Decorative divider line -- marked as an Artifact so a screen reader
  // skips it instead of it showing up as untagged content.
  doc.markContent("Artifact", { type: "Layout" });
  doc
    .moveTo(pageLeft, doc.y)
    .lineTo(pageRight, doc.y)
    .lineWidth(0.5)
    .strokeColor("#000000")
    .stroke();
  doc.endMarkedContent();
  doc.moveDown(0.5);

  // Everything below -- the gold bar, the transactions table, the
  // "Report Generated" line, the footer bar, and the disclaimer -- only
  // renders when there's actual data.
  if (hasData) {
    const firstBarHeight = 12;
    const firstBarTop = doc.y + 12;
    doc.markContent("Artifact", { type: "Layout" });
    doc
      .rect(pageLeft, firstBarTop, contentWidth, firstBarHeight)
      .fill("#CCA75E");
    doc.endMarkedContent();

    // 5. Transactions table
    const paddingFor = (colIndex) =>
      columnAlignments[colIndex] === "left"
        ? { top: 1.5, right: 3, bottom: 1.5, left: 3 }
        : { top: 1.5, right: RIGHT_PADDING, bottom: 1.5, left: CELL_PADDING };
    const alignFor = (colIndex) => ({
      x: columnAlignments[colIndex],
      y: "center",
    });

    await doc.table({
      structParent: documentStruct,
      position: { x: pageLeft, y: firstBarTop + 12 },
      columnStyles: columnWidths,
      rowStyles: (i) =>
        i === 0
          ? {
              backgroundColor: "#03002F",
              textColor: "#ffffff",
              font: { src: "Body-Bold.ttf", size: TABLE_FONT_SIZE },
              padding: CELL_PADDING,
              border: CELL_BORDER,
            }
          : {
              font: { src: "Body-Regular.ttf", size: TABLE_FONT_SIZE },
              textColor: "#000000",
              padding: CELL_PADDING,
              border: CELL_BORDER,
            },
      data: [
        headers.map((h, colIndex) => ({
          text: h,
          type: "TH",
          scope: "column",
          align: alignFor(colIndex),
          padding: paddingFor(colIndex),
        })),
        ...rows.map((row, rowIndex) =>
          row.map((cell, colIndex) => ({
            text: cell,
            align: alignFor(colIndex),
            padding: paddingFor(colIndex),
            ...(rowIsPrimary[rowIndex]
              ? { backgroundColor: PRIMARY_ROW_BACKGROUND_COLOR }
              : {}),
          })),
        ),
      ],
    });
    doc.moveDown(0.3);
    const generatedTextTop = doc.y + 12;

    documentStruct.add(
      doc.struct("P", () => {
        doc
          .font("Body-Bold.ttf")
          .fontSize(TABLE_FONT_SIZE)
          .fillColor("#000000")
          .text(getGeneratedTimeAndDate(), pageLeft, generatedTextTop, {
            width: contentWidth,
            align: "right",
          });
      }),
    );
    doc.moveDown(0.4);

    // 6. Decorative gold footer bar
    const barHeight = 12;
    if (doc.y + barHeight > doc.page.maxY()) {
      doc.addPage();
    }
    const barTop = doc.y;

    doc.markContent("Artifact", { type: "Layout" });
    doc.rect(pageLeft, barTop, contentWidth, barHeight).fill("#cca75e");
    doc.endMarkedContent();

    // 7. Disclaimer paragraph
    let disclaimerTop = barTop + 2;
    let linkTop = disclaimerTop + 12;

    if (
      fileId === "recentTransactionId" ||
      fileId === "recentTransactionCorporateId"
    ) {
      disclaimerTop = barTop + 13;
      documentStruct.add(
        doc.struct("P", () => {
          doc
            .font("Body-Regular.ttf")
            .fontSize(TABLE_FONT_SIZE)
            .fillColor("#333333")
            .text(
              `The  ${
                fileId === "recentTransactionId"
                  ? "transactions report"
                  : "corporate actions report"
              } will consist of the recent 100 transactions for the selected date range. Please change the same to extract the older transactions or extract in excel.`,
              pageLeft,
              disclaimerTop,
              { width: contentWidth },
            );
        }),
      );
      linkTop = doc.y + 1;
    }

    // 8. "Master Disclaimer" link
    documentStruct.add(
      doc.struct("Link", () => {
        doc
          .font("Body-Regular.ttf")
          .fontSize(TABLE_FONT_SIZE)
          .fillColor("#485FC7")
          .text(disclaimer.title, pageLeft, linkTop, {
            link: disclaimer.link,
            //underline: true,
          });
      }),
    );
  }

  doc.fillColor("#000000");

  documentStruct.end();

  doc.end();
  return done;
}
